// moduleActions.js — ENGINE-synthesized AI actions for workspace modules,
// gated by HUMAN APPROVAL. Generic replacement for the flow the legacy v1
// GreenIG module pioneered (retired with the WS6 migration, 2026-07-30):
// the assistant never mutates module data directly.
//
// A manifest-v2 module declares `actions: { key → { validate, summarize, run } }`
// (plain functions over (root, args), revived from the published project data).
// The loader (workspaceProjects.js) injects the trio built here into
// context.functions when the module activates — and tears it down with the
// rest of the module's keys:
//
//   proposeModuleAction(actionKey, args)
//     1. validates + summarizes the action,
//     2. parks { id, key, args, summary } on root._aiAction,
//     3. pushes an assistant message carrying an `aiAction` card
//        (Approve / Reject — rendered by ChatCard's PvCardAiAction).
//   approveAiAction()  — AWAITS actions[key].run(root, args, el) (runs may be
//                        async; legacy 2-arg sync runs work unchanged), clears
//                        the park (even on failure), confirms in chat.
//   rejectAiAction()   — clears the park, acknowledges in chat.
//
// Nothing changes until the user clicks Approve — every action needs a click.
// All three are (el, s)-style `this`-bound functions, matching how el.call
// invokes registered functions. The fn NAMES are appended to the module's
// stashed aiFunctions so simoneAiTransport's module gate accepts them.
//
// WS-MODULE-ACTIONS-RUN-ON-THE-SHELL-ROOT-NOT-THE-MODULE-STORE-1 — the
// INVOKING element is shell chrome (the chat / approval card), so its own
// `state.root` is the SHELL root, not the module's own state root
// (WS-MODULE-STATE-SCOPE-1). The park (`_aiAction`) and the chat message stay
// on that shell root on purpose — the approval UI is shell chrome and reads
// them there. But `def.validate` / `def.summarize` / `def.run` must see the
// OWNING module's loaded data, so the optional `resolveOwnerRoot(actionKey,
// shellRoot)` callback (passed by the loader, which alone knows which module
// declared each action key) resolves that module's store; a missing/null
// result falls back to the shell root, same soft-fail as an unscoped module.

import { readOnlyCallOf, refuseInReadOnlyCall } from './moduleReadOnlyCall.js'
import { scopeHostState, shellRootOf } from './moduleStateScope.js'

// Chat-scope key resolution — which chatMessages bag the approval card lands
// in ('' = simone drawer, '_db_' = dashboard thread, '_ax_' = add-app).
const _resolveCk = (el) => {
  let n = el
  while (n) {
    if (n.chatScope) {
      const s = n.chatScope
      if (s === 'addapp') return '_ax_'
      if (s === 'dashboard') return '_db_'
      return ''
    }
    n = n.parent
  }
  const root = el && el.state && el.state.root
  if (!root) return ''
  if (root._db_currentAssistantId) return '_db_'
  return ''
}

// The SHELL's root — where the park and its card live — even while the
// element is scoped to a module store (an approved run scopes the card's own
// state to the owning store; see approveAiAction).
const _actionsRootOf = (el) =>
  shellRootOf((el && el.state && el.state.root) || (el && el.getRoot && el.getRoot().state))

const _pushAssistant = (el, root, text, cards) => {
  const k = _resolveCk(el)
  const msg = {
    id: `a-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    role: 'assistant',
    text,
    ts: Date.now()
  }
  if (cards) msg.cards = cards
  root.update({ [k + 'chatMessages']: (root[k + 'chatMessages'] || []).concat(msg) })
}

// Copy resolution for the approval trio. These fns are reached as
// `el.call('proposeModuleAction', …)`, so `this` is the element — but the AI
// transport also invokes them off a bare stash in tests and in the module
// sandbox, where there is no element to reach polyglot through. Return the KEY
// in that case rather than throwing; a raw key in a headless call site is
// harmless, an exception is not.
const _t = (el, key) => (el && typeof el.call === 'function' ? el.call('polyglot', key) : key)

// What an app's `summarize` gets to write the approval card's line in the
// person's language (a Georgian session showed an English line: summarize had
// no way to reach polyglot). `t(key, vars)` — the shell's polyglot, `{name}`
// placeholders filled from `vars`; `lang` — the session language. A
// two-argument summarize ignores it and keeps working.
const _summaryContext = (el) => {
  let lang = ''
  try {
    lang = el && typeof el.call === 'function' ? String(el.call('getActiveLang') || '') : ''
  } catch (_) {
    lang = ''
  }
  return {
    lang,
    t: (key, vars) => {
      let out = String(_t(el, key))
      if (vars && typeof vars === 'object') {
        for (const name of Object.keys(vars)) out = out.split(`{${name}}`).join(String(vars[name]))
      }
      return out
    }
  }
}

// The names of the fns buildModuleActionFns returns (injected into the
// module context with the rest of its functions).
export const MODULE_ACTION_FN_NAMES = ['proposeModuleAction', 'approveAiAction', 'rejectAiAction']

// The ONE of them the assistant may call — appended to the module's
// aiFunctions stash so the AI transport gate accepts it at call time. Approve
// and reject are the MEMBER's clicks (ChatCard's PvCardAiAction calls them
// directly): listed as AI-callable, the model could approve its own proposal,
// with no click at all in Auto mode (MODULE-ACTIONS-PROPOSAL-NEVER-OFFERED-1).
export const MODULE_ACTION_AI_FNS = ['proposeModuleAction']

// The action keys of a manifest `actions` registry — real action defs only
// (a revived `run` or `validate`), never serializer metadata (`__order`). The
// same rule loadWorkspaceApps' merge applies.
export const moduleActionKeys = (actions) =>
  Object.entries(actions && typeof actions === 'object' ? actions : {})
    .filter(
      ([, def]) =>
        def &&
        typeof def === 'object' &&
        (typeof def.run === 'function' || typeof def.validate === 'function')
    )
    .map(([key]) => key)

// An action's own words for the assistant: its declared `describe` string, if
// any (`actions.<key>.describe`), capped — the catalog rides every turn.
export const moduleActionDocs = (actions) => {
  const out = {}
  for (const key of moduleActionKeys(actions)) {
    const def = actions[key]
    const text = typeof def.describe === 'string' ? def.describe.trim() : ''
    if (text) out[key] = text.slice(0, 300)
  }
  return out
}

// An action's NAME for a person (the composer's capabilities answer, "Do"):
// its declared `label` — a bundle key the app ships or plain words — capped.
// An action that declares none is absent here; the reader derives words from
// its key ("mintTokens" → "Mint tokens") so a row never shows a raw key.
export const moduleActionLabels = (actions) => {
  const out = {}
  for (const key of moduleActionKeys(actions)) {
    const def = actions[key]
    const text = typeof def.label === 'string' ? def.label.trim() : ''
    if (text) out[key] = text.slice(0, 80)
  }
  return out
}

// What an approved run answered, in words for the member. A run may return:
//   a string                          → done, in the app's words (as always)
//   { ok: false, error? | message? }  → NOT applied, in the app's words — no ✓
//   { message } / { ok: true, message } → done, in the app's words
//   nothing, or an object with no words → the shell's own "Done."
// A run that throws stays the catch path ("could not be applied"). Before
// this, an object answer printed "✓ [object Object]" and a run could report a
// no-op only by throwing, so "No incident … — nothing changed." wore the
// success mark (STORM-VAL-ACK-INCIDENT-ACTION-1 measure, dev 2026-10-04).
const _words = (v) => (typeof v === 'string' && v.trim() ? v.trim() : '')
// A run may name the page its write landed on — `{ message, open: '/orders' }`
// — and the card offers Open there. Only a same-origin path rides along: never
// a URL, never a protocol-relative `//host` (nor `/\host`, which browsers read
// as one).
const _openPath = (v) => (typeof v === 'string' && /^\/(?![/\\])/.test(v) ? v : '')
const _runOutcome = (el, out) => {
  const done = _t(el, 'workspace.ai.actionDone')
  if (typeof out === 'string') return { ok: true, text: _words(out) || done }
  if (out && typeof out === 'object' && !Array.isArray(out)) {
    if (out.ok === false) {
      return {
        ok: false,
        text: _words(out.error) || _words(out.message) || _t(el, 'workspace.ai.actionNotApplied')
      }
    }
    const text = _words(out.message) || done
    const open = _openPath(out.open)
    return open ? { ok: true, text, open } : { ok: true, text }
  }
  return { ok: true, text: done }
}

// THE CARD'S OUTCOME (APPROVAL-CARD-SHOWS-HANDLED-AFTER-A-REFUSAL-1). Once the
// park clears, the approval card used to read "✓ Handled" whatever happened:
// measured on dev 2026-10-05 (Storm Tokens) — an approved action the app
// REFUSED wrote its own words in chat with no ✓, while the card above it still
// said "✓ Handled". The outcome now rides the card itself, written in the SAME
// update that clears the park (so the card never shows a check mark first):
//
//   card.outcome = { state: 'handled',    text }  — the run changed something;
//                                                   `text` is the app's words
//                                                   (or the shell's "Done.")
//                | { state: 'notApplied', text }  — the run answered ok:false
//                                                   (`text` = its reason) or
//                                                   threw (`text` = the shell's
//                                                   "could not be applied")
//                | { state: 'rejected' }         — the member clicked Reject
//
// ChatCard's PvCardAiAction reads `card.outcome`; a handled card without one
// (a card from before this field) keeps the plain "✓ Handled". The card lives
// in whichever chat bag the proposal landed in, so every bag is searched.
const OUTCOME_CHAT_BAGS = ['', '_db_', '_ax_']
const _isCard = (c, id) => Boolean(c) && c.type === 'aiAction' && c.id === id
export const cardOutcomePatch = (root, cardId, outcome) => {
  const patch = {}
  if (!root || !cardId || !outcome) return patch
  for (const k of OUTCOME_CHAT_BAGS) {
    const list = root[k + 'chatMessages']
    if (!Array.isArray(list)) continue
    let hit = false
    const next = list.map((m) => {
      if (!m || !Array.isArray(m.cards) || !m.cards.some((c) => _isCard(c, cardId))) return m
      hit = true
      return { ...m, cards: m.cards.map((c) => (_isCard(c, cardId) ? { ...c, outcome } : c)) }
    })
    if (hit) patch[k + 'chatMessages'] = next
  }
  return patch
}

// ONE LINE PER OUTCOME (APPROVAL-OUTCOME-PRINTED-TWICE-1, decided 2026-10-05).
// The card carries the outcome — "✓ <the app's words>", "✗ Not applied:
// <reason>", "Rejected" (ChatCard PvAiHandled) — so the chat gets no second
// line saying the same thing: measured on dev (Storm Tokens), every approved
// action printed "✓ Handled" on its card AND "✓ <words>" under it. Only when no
// card shows the outcome (the bag that held it is gone) does it fall back to
// one chat line, so an outcome is never silent.
const _outcomeLine = (el, outcome) => {
  if (!outcome) return ''
  if (outcome.state === 'rejected') return _t(el, 'workspace.ai.actionCancelled')
  if (outcome.state === 'handled') return `✓ ${outcome.text}`
  return outcome.text || ''
}
const _landOutcome = (el, root, cardId, outcome) => {
  const patch = cardOutcomePatch(root, cardId, outcome)
  root.update({ _aiAction: null, ...patch })
  if (Object.keys(patch).length) return
  const line = _outcomeLine(el, outcome)
  if (line) _pushAssistant(el, root, line)
}

// Build the injectable approval-gated trio over a manifest `actions` registry.
// `resolveOwnerRoot(actionKey, shellRoot)`, when given, returns the OWNING
// module's own state store for validate/summarize/run — omit it (or return a
// falsy value for a given key) to run against `shellRoot`, unchanged.
export const buildModuleActionFns = (actions = {}, resolveOwnerRoot) => {
  // One run per park: a second Approve, or a Reject, while the approved run is
  // in flight changes nothing — the run's own outcome lands when it ends.
  const inFlight = new Set()

  // Propose an action — parks it + shows the Approve/Reject card. Returns a
  // result object the cloud assistant reads (so it knows it's awaiting the
  // human).
  function proposeModuleAction(actionKey, args) {
    const el = this
    // A verb marked readOnly never proposes a change (MODULE-FN-READONLY-ENFORCED-1).
    const readOnlyCall = readOnlyCallOf(el)
    if (readOnlyCall) return refuseInReadOnlyCall(readOnlyCall, 'proposeModuleAction', [actionKey])
    const root = _actionsRootOf(el)
    if (!root || typeof root.update !== 'function') {
      return { ok: false, error: _t(el, 'workspace.ai.noWorkspaceUi') }
    }
    const def = actions[actionKey]
    if (!def) {
      return {
        ok: false,
        error: _t(el, 'workspace.ai.unknownAction').replace('{action}', actionKey)
      }
    }
    const dataRoot =
      (typeof resolveOwnerRoot === 'function' && resolveOwnerRoot(actionKey, root)) || root
    const a = args || {}
    if (typeof def.validate === 'function') {
      const err = def.validate(dataRoot, a)
      if (err) return { ok: false, error: err }
    }

    // `this` = the element, third argument = _summaryContext (the line in the
    // person's language).
    const summary =
      typeof def.summarize === 'function'
        ? def.summarize.call(el, dataRoot, a, _summaryContext(el))
        : String(actionKey)
    const id = `act-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
    root.update({ _aiAction: { id, key: actionKey, args: a, summary } })

    const k = _resolveCk(el)
    const msg = {
      id: `a-${id}`,
      role: 'assistant',
      text: _t(el, 'workspace.ai.approvalPrompt'),
      ts: Date.now(),
      cards: [{ type: 'aiAction', id, summary }]
    }
    root.update({ [k + 'chatMessages']: (root[k + 'chatMessages'] || []).concat(msg) })
    return {
      ok: true,
      pending: true,
      summary,
      message: _t(el, 'workspace.ai.awaitingApproval')
    }
  }

  async function approveAiAction() {
    const el = this
    const readOnlyCall = readOnlyCallOf(el)
    if (readOnlyCall) return refuseInReadOnlyCall(readOnlyCall, 'approveAiAction')
    const root = _actionsRootOf(el)
    const pa = root && root._aiAction
    if (!pa) return { ok: false, error: _t(el, 'workspace.ai.nothingToApprove') }
    if (inFlight.has(pa.id)) return { ok: false, running: true }
    const def = actions[pa.key]
    const dataRoot =
      (typeof resolveOwnerRoot === 'function' && resolveOwnerRoot(pa.key, root)) || root
    // THE OWNER'S SCOPE FOR THE RUN (BESSI dev 2026-10-28). run() gets the
    // owning store, but an app run reaches its data plane through the element
    // it is handed — el.call('bsCreate', …) writes `this.state.root` — and that
    // element is the card's Approve button, whose root is the SHELL's: the
    // approved draft order landed on the shell root and the module's /orders
    // never showed it. For the run, the card's own state reads the owning store
    // as its root (scopeHostState, as a home widget frame scopes its module
    // host); it gets its own root back before the outcome lands.
    const ownRoot = el && el.state ? el.state.root : null
    const scoped = dataRoot !== root && scopeHostState(el, dataRoot)
    inFlight.add(pa.id)
    // The card's outcome (cardOutcomePatch) — set on every path, landed with
    // the park's clear below, and the ONE place the outcome shows.
    let cardOutcome = null
    try {
      let out
      if (def && typeof def.run === 'function') {
        // run(root, args, el) — the invoking element rides along so async
        // actions can reach their data plane (el.call('moduleData', …)).
        // Awaiting tolerates both shapes: a legacy sync 2-arg run returns its
        // string (awaiting a non-promise is a no-op), an async run resolves it.
        out = await def.run(dataRoot, pa.args, el)
      }
      const outcome = _runOutcome(el, out)
      // Only a change that happened wears the check mark; a run that answers
      // { ok: false } speaks in its own words, with none.
      cardOutcome = {
        state: outcome.ok ? 'handled' : 'notApplied',
        text: outcome.text,
        ...(outcome.open ? { open: outcome.open } : {})
      }
      return outcome.ok ? { ok: true, result: outcome.text } : { ok: false, error: outcome.text }
    } catch (e) {
      // A rejected run must never strand the parked action (finally clears
      // it) — the card says it was not applied instead of silently swallowing.
      console.warn('[moduleActions] approved action failed:', pa.key, e && e.message)
      cardOutcome = { state: 'notApplied', text: _t(el, 'workspace.ai.actionNotApplied') }
      return { ok: false, error: (e && e.message) || _t(el, 'workspace.ai.actionFailed') }
    } finally {
      inFlight.delete(pa.id)
      if (scoped) scopeHostState(el, ownRoot)
      _landOutcome(el, root, pa.id, cardOutcome)
    }
  }

  function rejectAiAction() {
    const el = this
    const readOnlyCall = readOnlyCallOf(el)
    if (readOnlyCall) return refuseInReadOnlyCall(readOnlyCall, 'rejectAiAction')
    const root = _actionsRootOf(el)
    if (!root) return { ok: false }
    const pa = root._aiAction
    // The approved run already started: a Reject now cancels nothing.
    if (pa && inFlight.has(pa.id)) return { ok: false, running: true }
    if (pa) _landOutcome(el, root, pa.id, { state: 'rejected' })
    else root.update({ _aiAction: null })
    return { ok: true, cancelled: true }
  }

  return { proposeModuleAction, approveAiAction, rejectAiAction }
}
